import { errorCode, type TrayItem } from '@sd/client-core';
import { LIMITS, type ConversationSummary, type Person } from '@sd/contracts';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatBytes, formatWhen, scheduleChoices } from '../format';
import { Icon } from '../icons/Icon';
import { Button, IconButton } from '../ui/controls';
import { usePopover, useToast } from '../ui/overlays';
import { Avatar } from '../ui/Avatar';
import { enterSends, isComposing, LONG_PRESS_MS } from '../ui/keys';
import { FileIcon } from './attachments';
import { useAppEnv, useClient, useClientState } from './context';
import { errorMessage } from '../errors';
import { conversationName, editFailedMessage, isSelfChat, unscannableReason, uploadLimitsText } from './copy';
import { BP } from './layout';
import { useShell } from './shell';

const EMOJI = '😀 😂 🥹 😍 🙂 😉 🙌 👍 👏 🙏 🎉 🔥 ✅ ❗ 💡 📌 📎 🗓️ ☕ 🍜 🚀 ❤️ 👀 🤔'.split(' ');

/** `@` and the name typed so far, right before the caret: a mention to complete. An e-mail address is not one. */
export function mentionAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf('@');
  if (start < 0 || (start > 0 && !/\s/.test(before[start - 1]!))) return null;
  const query = before.slice(start + 1);
  return query.length <= 20 && !/\s/.test(query) ? { start, query } : null;
}

/** Members whose name (first) or team holds the query, at most six. */
function mentionCandidates(members: readonly Person[], query: string): Person[] {
  const q = query.toLocaleLowerCase('ko');
  const active = members.filter((p) => p.status !== 'deactivated');
  const named = active.filter((p) => p.displayName.toLocaleLowerCase('ko').includes(q));
  const named1 = named.filter((p) => p.displayName.toLocaleLowerCase('ko').startsWith(q));
  const team = active.filter((p) => !named.includes(p) && p.department.toLocaleLowerCase('ko').includes(q));
  return [...named1, ...named.filter((p) => !named1.includes(p)), ...team].slice(0, 6);
}

/** "이도윤 님이 입력 중", "이도윤, 박지안 님이 입력 중", "이도윤 님 외 2명이 입력 중": the people typing in a conversation. */
export function typingText(names: readonly string[]): string | null {
  if (!names.length) return null;
  if (names.length === 1) return `${names[0]} 님이 입력 중`;
  if (names.length === 2) return `${names[0]}, ${names[1]} 님이 입력 중`;
  return `${names[0]} 님 외 ${names.length - 1}명이 입력 중`;
}

/** Who else is typing here, over the input; visual only (a screen reader is not interrupted for it). */
function TypingLine({ conversationId }: { conversationId: string }) {
  const typing = useClientState((s) => s.typing[conversationId]);
  const people = useClientState((s) => s.people);
  const text = typingText(Object.keys(typing ?? {}).flatMap((id) => (people[id] ? [people[id].displayName] : [])));
  if (!text) return null;
  return (
    <div className="typing-line" aria-hidden="true">
      {text}
      <span className="typing-dots">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

/** Two-digit "2026-10-28T09:00" for a datetime-local input, in the viewer's time. */
function localInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** "예약 전송": the usual times, or one picked; files and the reply do not go with it. */
function SchedulePicker({ now, leftOut, onPick }: { now: Date; leftOut: boolean; onPick(at: Date): void }) {
  const [custom, setCustom] = useState('');
  const min = new Date(now.getTime() + 60_000);
  const max = new Date(now.getTime() + LIMITS.scheduleAheadDays * 86_400_000);
  return (
    <>
      <h2 aria-hidden="true">보낼 시각</h2>
      {scheduleChoices(now).map((at) => (
        <button key={at.getTime()} type="button" className="pi" onClick={() => onPick(at)}>
          <Icon name="clock" size="sm" />
          {formatWhen(at, now)}
        </button>
      ))}
      <form
        className="sched-custom"
        onSubmit={(event) => {
          event.preventDefault();
          if (custom) onPick(new Date(custom));
        }}
      >
        <input type="datetime-local" aria-label="보낼 날짜와 시각" value={custom} min={localInput(min)} max={localInput(max)} onChange={(event) => setCustom(event.target.value)} />
        <Button size="sm" type="submit" disabled={!custom}>
          예약
        </Button>
      </form>
      {leftOut ? <p className="hint">답장과 첨부 파일은 함께 예약되지 않아요. 글만 보내요</p> : null}
    </>
  );
}

const isBusy = (item: TrayItem) => item.state === 'uploading' || item.state === 'scanning';

function TrayRow({ item, onRemove, onRetry }: { item: TrayItem; onRemove(): void; onRetry(): void }) {
  const status =
    item.state === 'ready' ? (
      <span className="scan ok">
        <Icon name="checkc" />
        업로드 완료
      </span>
    ) : item.state === 'uploading' ? (
      <span className="scan wait">
        <Icon name="sync" className="spin" />
        올리는 중 {item.progress}%
      </span>
    ) : item.state === 'scanning' ? (
      <span className="scan wait">
        <Icon name="sync" className="spin" />
        악성코드 검사 중…
      </span>
    ) : item.state === 'unscannable' ? (
      <span className="scan warn">
        <Icon name="alert" />
        {unscannableReason(item.file?.scanDetail)} 악성코드 검사를 할 수 없어요
      </span>
    ) : item.state === 'infected' ? (
      <span className="scan bad">
        <Icon name="alertc" />
        악성코드가 발견돼 보낼 수 없어요
      </span>
    ) : (
      <span className="scan bad">
        <Icon name="alertc" />
        {errorMessage(item.error)}
      </span>
    );
  const busy = isBusy(item);
  const pct = item.state === 'scanning' ? (item.progress >= 100 ? 70 : item.progress) : item.progress;
  return (
    <div className={`ti${item.state === 'unscannable' || item.state === 'infected' || item.state === 'failed' ? ' warn' : ''}`}>
      <FileIcon ext={item.ext} />
      <div className="ti-main">
        <div className="ti-name ell" title={item.name}>
          {item.name}
        </div>
        <div className="ti-st">
          {formatBytes(item.size)} · {status}
          {busy && item.sendWhenReady ? ' · 끝나면 보내요' : null}
        </div>
        {busy ? (
          <div className="prog" style={{ marginTop: 6 }}>
            <i style={{ width: `${pct || 30}%` }} />
          </div>
        ) : null}
      </div>
      {/* After a restart the picked file is gone: only attaching it again helps. */}
      {item.state === 'failed' && item.error !== 'ATTACH_AGAIN' ? <IconButton icon="retry" iconSize="sm" label={`${item.name} 다시 올리기`} tip="다시 올리기" onClick={onRetry} /> : null}
      <IconButton icon="x" iconSize="sm" label={`${item.name} 첨부 취소`} onClick={onRemove} />
    </div>
  );
}

/** Composer area of the chat pane: context bar, attachment tray, input, read-only bar. */
export function Composer({ conversation }: { conversation: ConversationSummary }) {
  const client = useClient();
  const shell = useShell();
  const popover = usePopover();
  const toast = useToast();
  const id = conversation.id;
  const draft = useClientState((s) => s.drafts[id] ?? '');
  const tray = useClientState((s) => s.trays[id]);
  const details = useClientState((s) => s.details[id]);
  const people = useClientState((s) => s.people);
  const meId = useClientState((s) => s.me?.id ?? '');
  const timeline = useClientState((s) => s.timelines[id]);
  const limits = useClientState((s) => s.serverInfo?.limits ?? LIMITS);
  const scheduleOn = useClientState((s) => s.serverInfo?.capabilities.includes('messages.scheduled') ?? false);
  const scheduledAll = useClientState((s) => s.scheduled);
  const env = useAppEnv();
  const [text, setText] = useState(draft);
  const textRef = useRef(text);
  textRef.current = text;
  const savingEdit = useRef(false);
  const typingBeforeSend = useRef(false);
  const holdTimer = useRef(0);
  const held = useRef(false);
  useEffect(() => () => window.clearTimeout(holdTimer.current), []);
  const editing = shell.composer.editing;
  const fileInput = useRef<HTMLInputElement>(null);
  const inputRef = shell.composer.inputRef;
  const readOnly = conversation.readOnly;
  const contextId = useId();
  const suggestId = useId();
  const [caret, setCaret] = useState<number | null>(null);
  const [pick, setPick] = useState({ query: '', index: 0 });
  const [dismissed, setDismissed] = useState<number | null>(null);

  // Load the draft when switching conversations; keep the old one as a draft.
  const lastId = useRef(id);
  useEffect(() => {
    if (lastId.current !== id) {
      client.setDraft(lastId.current, textRef.current.trim() ? textRef.current : '');
      lastId.current = id;
      setText(client.state.drafts[id] ?? '');
    }
  }, [client, id]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!editing) client.setDraft(id, text.trim() ? text : '');
    }, 400);
    return () => window.clearTimeout(timer);
  }, [client, id, text, editing]);

  // The web keeps the draft only in this page and the client gets it 400 ms after typing stops (or never, while editing):
  // leaving with text in the input asks first, like MessengerApp does for the rest.
  const web = env.platform.kind === 'web';
  useEffect(() => {
    if (!web) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (textRef.current.trim()) event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [web]);

  // The messenger opens with focus on the page body (the screen before it is gone), where a screen reader
  // says nothing: start in the message input, as other chat apps do.
  const showing = !shell.single || shell.view === 'chat';
  useEffect(() => {
    const active = document.activeElement;
    if (showing && (!active || active === document.body)) inputRef.current?.focus();
  }, [showing, inputRef]);

  // Starting an edit puts the original text into the input; later timeline changes (receipts, new messages) must not reset what is typed.
  useEffect(() => {
    if (!editing) return;
    const m = client.state.timelines[id]?.messages.find((x) => x.id === editing);
    if (m?.text) setText(m.text);
  }, [client, id, editing]);

  const autosize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = '40px';
    if (el.value) el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  };
  useEffect(autosize, [text, inputRef]);

  const members = useMemo(() => {
    if (details) return details.members.map((m) => m.person).filter((p) => p.id !== meId);
    if (conversation.peer) return [conversation.peer];
    return conversation.memberPreview;
  }, [details, conversation.peer, conversation.memberPreview, meId]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    if (editing) {
      if (savingEdit.current) return;
      savingEdit.current = true;
      try {
        await client.editMessage(id, editing, value);
      } catch (error) {
        toast(editFailedMessage(errorCode(error)));
        return;
      } finally {
        savingEdit.current = false;
      }
      shell.composer.cancel();
      setText(shell.composer.editPrev);
      toast('메시지를 수정했어요');
      return;
    }
    const mentions = members.filter((p) => value.includes(`@${p.displayName}`)).map((p) => p.id);
    client.sendMessage(id, value, { replyToMessageId: shell.composer.replyTo, mentions });
    shell.composer.setReplyTo(null);
    setText('');
  };

  // @ followed by a name opens the people to mention above the input; the keyboard stays in the input.
  const typed = caret === null ? null : mentionAt(text, caret);
  const suggestions = typed && typed.start !== dismissed ? mentionCandidates(members, typed.query) : [];
  const picked = typed && pick.query === typed.query ? Math.min(pick.index, suggestions.length - 1) : 0;
  const completeMention = (person: Person) => {
    if (!typed || caret === null) return;
    const snippet = `@${person.displayName} `;
    setText(text.slice(0, typed.start) + snippet + text.slice(caret));
    const at = typed.start + snippet.length;
    setCaret(at);
    window.setTimeout(() => {
      const el = inputRef.current;
      el?.focus();
      if (el) el.selectionStart = el.selectionEnd = at;
    }, 0);
  };

  const scheduleAt = (at: Date, close: () => void) => {
    const value = text.trim();
    if (!value) return;
    const mentions = members.filter((p) => value.includes(`@${p.displayName}`)).map((p) => p.id);
    client.scheduleMessage(id, value, at, mentions).then(
      () => {
        close();
        setText('');
        toast(`${formatWhen(at, env.now())}에 보낼게요`, { icon: 'clock' });
      },
      (error: unknown) => toast(errorCode(error) === 'VALIDATION_FAILED' ? `1분 뒤부터 ${LIMITS.scheduleAheadDays}일 안의 시각을 골라 주세요` : errorMessage(error)),
    );
  };
  const openSchedule = (anchor: HTMLElement) =>
    popover.open(anchor, (close) => <SchedulePicker now={env.now()} leftOut={Boolean(shell.composer.replyTo || tray?.length)} onPick={(at) => scheduleAt(at, close)} />, {
      align: 'right',
      role: 'dialog',
      label: '예약 전송',
    });
  const waiting = Object.values(scheduledAll)
    .filter((s) => s.conversationId === id)
    .sort((a, b) => a.sendAt.localeCompare(b.sendAt));
  const openWaiting = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      (close) => (
        <>
          <h2 aria-hidden="true">예약된 메시지</h2>
          {waiting.map((s) => (
            <div key={s.id} className="sched-item">
              <div className="sched-main">
                <b>{formatWhen(s.sendAt, env.now())}</b>
                <span className="ell">{s.text}</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  client.cancelScheduled(s.id).then(
                    () => {
                      close();
                      toast('예약을 취소했어요. 보내지 않아요');
                    },
                    (error: unknown) => toast(errorCode(error) === 'CONFLICT' ? '이미 보냈거나 보내지 못한 메시지예요' : errorMessage(error)),
                  )
                }
              >
                예약 취소
              </Button>
            </div>
          ))}
        </>
      ),
      { align: 'left', role: 'dialog', label: '예약된 메시지' },
    );

  const insertAt = (snippet: string) => {
    const el = inputRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = text.slice(0, start) + snippet + text.slice(end);
    setText(next);
    window.setTimeout(() => {
      el?.focus();
      if (el) el.selectionStart = el.selectionEnd = start + snippet.length;
    }, 0);
  };
  useEffect(() => shell.composer.registerInsert(insertAt));

  const openEmoji = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      () => (
        <div className="emoji-grid">
          {EMOJI.map((e) => (
            <button key={e} type="button" aria-label={e} onClick={() => insertAt(e)}>
              {e}
            </button>
          ))}
        </div>
      ),
      { align: 'right', role: 'dialog', label: '이모지' },
    );
  const openMention = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      (close) => (
        <>
          <h2 aria-hidden="true">멘션할 사람</h2>
          {members.map((p) => (
            <button
              key={p.id}
              type="button"
              role="menuitem"
              className="pi"
              onClick={() => {
                insertAt(`@${p.displayName} `);
                close();
              }}
            >
              <Avatar person={people[p.id] ?? p} noDot />
              {p.displayName} <small>{p.department}</small>
            </button>
          ))}
        </>
      ),
      { align: 'right', role: 'menu', label: '멘션할 사람' },
    );
  const pickFiles = (accept: string) => {
    if (!fileInput.current) return;
    fileInput.current.accept = accept;
    fileInput.current.click();
  };
  const openPlus = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      (close) => (
        <>
          <button type="button" role="menuitem" className="pi" onClick={() => (close(), pickFiles(''))}>
            <Icon name="clip" size="sm" />
            파일 첨부
          </button>
          <button type="button" role="menuitem" className="pi" onClick={() => (close(), pickFiles('image/*'))}>
            <Icon name="image" size="sm" />
            이미지
          </button>
          <button type="button" role="menuitem" className="pi" onClick={() => openEmoji(anchor)}>
            <Icon name="smile" size="sm" />
            이모지
          </button>
          <button type="button" role="menuitem" className="pi" onClick={() => openMention(anchor)}>
            <Icon name="at" size="sm" />
            멘션
          </button>
          {scheduleOn && text.trim() && !editing ? (
            <button type="button" role="menuitem" className="pi" aria-haspopup="dialog" onClick={() => openSchedule(anchor)}>
              <Icon name="clock" size="sm" />
              예약 전송
            </button>
          ) : null}
        </>
      ),
      { role: 'menu', label: '첨부·이모지·멘션' },
    );

  const items = tray ?? [];
  const busy = items.some(isBusy);
  // Ready files can go while others are still uploading or being scanned; those go by themselves once they are clean.
  const sendable = items.filter((i) => (i.state === 'ready' || i.state === 'unscannable') && i.file).length;
  const unmarked = items.filter((i) => isBusy(i) && !i.sendWhenReady).length;
  const replyMessage = shell.composer.replyTo ? timeline?.messages.find((m) => m.id === shell.composer.replyTo) : undefined;
  const placeholder = shell.width >= BP.laptop && shell.posture !== 'span-h' ? '메시지 입력 · Enter 전송, Shift+Enter 줄바꿈' : '메시지 입력';

  return (
    <div className="chat-bottom">
      <TypingLine conversationId={id} />
      <p className="tt-hint">테이블탑 모드 · 위 화면에서 대화를 보고, 아래에서 입력해요</p>
      <div className="tt-tools" style={readOnly ? { visibility: 'hidden' } : undefined}>
        <button type="button" onClick={() => pickFiles('image/*')}>
          <Icon name="image" size="lg" />
          사진
        </button>
        <button type="button" onClick={() => pickFiles('')}>
          <Icon name="clip" size="lg" />
          파일
        </button>
        <button type="button" aria-haspopup="dialog" onClick={(e) => openEmoji(e.currentTarget)}>
          <Icon name="smile" size="lg" />
          이모지
        </button>
        <button type="button" aria-haspopup="menu" onClick={(e) => openMention(e.currentTarget)}>
          <Icon name="at" size="lg" />
          멘션
        </button>
      </div>
      {!readOnly && waiting.length ? (
        <button type="button" className="sched-bar" aria-haspopup="dialog" onClick={(e) => openWaiting(e.currentTarget)}>
          <Icon name="clock" size="sm" />
          예약된 메시지 {waiting.length}개 · {formatWhen(waiting[0]!.sendAt, env.now())}
        </button>
      ) : null}
      <div className={`ctx-bar${!readOnly && (replyMessage || shell.composer.editing) ? ' show' : ''}`}>
        {replyMessage ? (
          <>
            <Icon name="reply" size="sm" />
            <span className="cb-txt ell" id={contextId}>
              <b>{replyMessage.senderId === meId ? '나' : (people[replyMessage.senderId ?? '']?.displayName ?? '')}</b>에게 답장 · {replyMessage.text ?? replyMessage.attachments[0]?.name ?? '사진'}
            </span>
            <IconButton icon="x" iconSize="sm" label="답장 취소" onClick={() => shell.composer.setReplyTo(null)} />
          </>
        ) : shell.composer.editing ? (
          <>
            <Icon name="compose" size="sm" />
            <span className="cb-txt ell" id={contextId}>
              <b>메시지 수정 중</b> · Esc로 취소
            </span>
            <IconButton
              icon="x"
              iconSize="sm"
              label="수정 취소"
              onClick={() => {
                setText(shell.composer.editPrev);
                shell.composer.cancel();
              }}
            />
          </>
        ) : null}
      </div>
      {!readOnly && items.length ? (
        <div className="tray glass show" aria-label="첨부 파일">
          <div className="tray-items">
            {items.map((item) => (
              <TrayRow key={item.localId} item={item} onRemove={() => client.removeTrayItem(id, item.localId)} onRetry={() => client.retryTrayItem(item.localId)} />
            ))}
          </div>
          <div className="tray-foot">
            <span className="limit">
              <Icon name="info" size="xs" /> {uploadLimitsText(limits)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (text.trim()) {
                  void send();
                  toast('텍스트를 먼저 보냈어요. 파일은 준비되면 ‘파일 보내기’로 보내 주세요');
                } else toast('보낼 텍스트가 없어요. 메시지를 입력하거나 파일이 준비될 때까지 기다려 주세요');
              }}
            >
              텍스트만 먼저 보내기
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon="send"
              disabled={!sendable && !unmarked}
              tip={busy ? (sendable ? '준비된 파일은 지금, 나머지는 검사가 끝나는 대로 보내요' : unmarked ? '검사가 끝나는 대로 보내요' : '검사가 끝나면 저절로 보내요') : undefined}
              onClick={() => {
                const { sent, waiting } = client.sendTray(id);
                if (sent && waiting) toast(`파일 ${sent}개를 보냈어요. 나머지 ${waiting}개는 검사가 끝나면 보낼게요`);
                else if (sent) toast(`파일 ${sent}개를 보냈어요`);
                else if (waiting) toast(`검사가 끝나면 파일 ${waiting}개를 보낼게요`);
              }}
            >
              {sendable ? `파일 보내기 ${sendable}` : '파일 보내기'}
            </Button>
          </div>
        </div>
      ) : null}
      {!readOnly && suggestions.length ? (
        <div className="mention-suggest" id={suggestId} role="listbox" aria-label="멘션할 사람">
          {suggestions.map((p, i) => (
            <div
              key={p.id}
              id={`${suggestId}-${i}`}
              role="option"
              aria-selected={i === picked}
              className={`pi${i === picked ? ' kb' : ''}`}
              // Keeps the focus (and the caret) in the input.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => completeMention(p)}
            >
              <Avatar person={people[p.id] ?? p} noDot />
              <span className="ell">{p.displayName}</span>
              <small className="ell">{p.department}</small>
            </div>
          ))}
        </div>
      ) : null}
      {!readOnly ? (
        <div className="composer glass-lite">
          <IconButton icon="plus" className="c-attach" label="파일 첨부" tip="파일 첨부" onClick={() => pickFiles('')} />
          <IconButton icon="plus" className="c-plus" label="첨부·이모지·멘션 메뉴" aria-haspopup="menu" onClick={(e) => openPlus(e.currentTarget)} />
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            placeholder={placeholder}
            aria-label={`${conversationName(conversation)}${conversation.kind === 'direct' && !isSelfChat(conversation) ? '에게' : '에'} 보낼 메시지 입력`}
            aria-describedby={replyMessage || shell.composer.editing ? contextId : undefined}
            aria-autocomplete="list"
            aria-controls={suggestions.length ? suggestId : undefined}
            aria-activedescendant={suggestions.length ? `${suggestId}-${picked}` : undefined}
            onChange={(e) => {
              setText(e.target.value);
              if (!editing && e.target.value.trim()) client.typing(id);
              setCaret(e.target.selectionStart);
              if (dismissed !== null && mentionAt(e.target.value, e.target.selectionStart)?.start !== dismissed) setDismissed(null);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
            onBlur={() => setCaret(null)}
            onKeyDown={(e) => {
              if (suggestions.length && typed && !isComposing(e)) {
                const move = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
                if (move) {
                  e.preventDefault();
                  setPick({ query: typed.query, index: (picked + move + suggestions.length) % suggestions.length });
                  return;
                }
                if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey && enterSends())) {
                  e.preventDefault();
                  completeMention(suggestions[picked]!);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  setDismissed(typed.start);
                  return;
                }
              }
              if (e.key === 'Enter' && !e.shiftKey && enterSends()) {
                // Korean IME: Enter while composing confirms the syllable, it must not send.
                if (isComposing(e)) return;
                e.preventDefault();
                void send();
              }
              if (e.key === 'Escape' && (shell.composer.replyTo || shell.composer.editing)) {
                e.preventDefault();
                e.stopPropagation();
                if (shell.composer.editing) setText(shell.composer.editPrev);
                shell.composer.cancel();
              }
            }}
            onPaste={(e) => {
              const files = [...(e.clipboardData?.files ?? [])];
              if (files.length) {
                e.preventDefault();
                client.addFiles(id, files);
              }
            }}
          />
          <div className="c-extra">
            <IconButton icon="image" label="이미지 첨부" tip="이미지" onClick={() => pickFiles('image/*')} />
            <IconButton icon="smile" label="이모지" tip="이모지" aria-haspopup="dialog" onClick={(e) => openEmoji(e.currentTarget)} />
            <IconButton icon="at" className="mention-btn" label="멘션" tip="멘션" aria-haspopup="menu" onClick={(e) => openMention(e.currentTarget)} />
            {scheduleOn ? (
              <IconButton
                icon="clock"
                label="예약 전송"
                tip={text.trim() && !editing ? '예약 전송' : '글을 쓰면 보낼 시각을 정할 수 있어요'}
                aria-haspopup="dialog"
                disabled={!text.trim() || Boolean(editing)}
                onClick={(e) => openSchedule(e.currentTarget)}
              />
            ) : null}
          </div>
          <button
            type="button"
            className="send-btn"
            aria-label="보내기"
            disabled={!text.trim()}
            // People go on typing after a send: the focus, and on a phone the keyboard, stay in the input.
            onPointerDown={(e) => {
              typingBeforeSend.current = document.activeElement === inputRef.current;
              held.current = false;
              // A phone has no clock button: holding send picks when it goes.
              if (!scheduleOn || editing || e.pointerType === 'mouse') return;
              const button = e.currentTarget;
              window.clearTimeout(holdTimer.current);
              holdTimer.current = window.setTimeout(() => {
                held.current = true;
                openSchedule(button);
              }, LONG_PRESS_MS);
            }}
            onPointerUp={() => window.clearTimeout(holdTimer.current)}
            onPointerCancel={() => window.clearTimeout(holdTimer.current)}
            onPointerLeave={() => window.clearTimeout(holdTimer.current)}
            onContextMenu={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (held.current) {
                held.current = false;
                return;
              }
              void send();
              if (typingBeforeSend.current) inputRef.current?.focus();
              typingBeforeSend.current = false;
            }}
          >
            <Icon name="send" />
          </button>
        </div>
      ) : null}
      <div className={`readonly-bar glass-lite${readOnly ? ' show' : ''}`}>
        {readOnly ? (
          <>
            <Icon name="lock" />
            <span>
              {conversation.readOnlyReason === 'peer_deactivated'
                ? '비활성화된 계정이라 메시지를 보낼 수 없어요. 기존 대화는 읽기 전용으로 남아요.'
                : conversation.readOnlyReason === 'left'
                  ? '나간 대화예요. 나간 시점까지의 대화만 읽을 수 있어요.'
                  : '읽기 전용 대화예요.'}
            </span>
          </>
        ) : null}
      </div>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          if (files.length) client.addFiles(id, files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
